import React, {useMemo, useState} from 'react';
import {View, Pressable, ActivityIndicator} from 'react-native';
import Typography from 'design-system/src/Components/Typography/Typography';
import Metrics from 'design-system/src/Theme/Metrics';
import CardProfileMainMenu, {
  CardProfileMainMenuProps,
} from 'design-system/src/Components/Card/CardHomeProfile/CardProfileMainMenu';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import ProfileMenu, {
  ProfileMenuProps,
} from 'design-system/src/Components/ProfileMenu/ProfileMenu.web';
import {useAppDispatch} from '@src/Store/hooks';
import useMember from '@src/Store/selectors/useMember';
import useFireBaseAuthentication from '@src/Hooks/useFirebaseAuthentication';
import memberSlice from '@src/Store/Slices/Member.slice';
import {Routes} from '@src/Constant/routes';
import useAppNavigation from '@src/utils/useAppNavigation';
import useResponsiveSpacing from '@src/Hooks/useResponsiveSpacing';
import defaultTheme from 'design-system/src/Theme/defaultTheme';
import {useCgvQuery} from '@src/Api/Graphql/gql/types.generated';
import {useSession} from '@src/Providers/SessionProvider';
import {CustomActivityIndicator} from 'design-system/src/Components/CustomActivityIndicator';
import useIsVodacomUser from '@src/Hooks/useIsVodacomUser';
import {VODACOM_TERMS_AND_CONDITIONS} from '@src/App/components/pages/TermsAndConditions/vodacomTerms';
import {trackTealiumSignout} from '@src/Tracking/tealiumEvents';

export type CgvData = {
  title: string;
  subtitle?: string;
  body: string;
};

export type CgvType = 'termsConditions' | 'privacyPolicy';

export const ProfileHome = () => {
  const {t} = useTranslation('profile');
  const [isLoading, setIsLoading] = useState(false);
  const appNavigation = useAppNavigation();
  const {gap, profile, isMobile} = useResponsiveSpacing();
  const session = useSession();
  const isVodacomUser = useIsVodacomUser();

  const {data} = useCgvQuery();

  const {
    termCondition,
    privacyPolicy,
  }: {termCondition?: CgvData; privacyPolicy?: CgvData} = useMemo(() => {
    let _termsConditions: CgvData | undefined;
    let _privacyPolicies: CgvData | undefined;
    if (
      data &&
      data.allSettings &&
      data.allSettings.items &&
      data.allSettings.items[0]
    ) {
      const {termsConditions, privacyPolicies} = data.allSettings.items[0];
      if (
        termsConditions &&
        termsConditions[0] &&
        termsConditions[0].__typename === 'TextPage'
      ) {
        const {title, body} = termsConditions[0];
        _termsConditions = {
          title: title || '',
          body: body || '',
        };
      }
      if (
        privacyPolicies &&
        privacyPolicies[0] &&
        privacyPolicies[0].__typename === 'TextPage'
      ) {
        const {title, body} = privacyPolicies[0];
        _privacyPolicies = {
          title: title || '',
          body: body || '',
        };
      }
    }
    return {
      termCondition: isVodacomUser
        ? VODACOM_TERMS_AND_CONDITIONS
        : _termsConditions,
      privacyPolicy: _privacyPolicies,
    };
  }, [data, isVodacomUser]);

  const member = useMember();
  const userName = useMemo(() => {
    return member ? `${member.firstName} ${member.lastName}` : '';
  }, [member]);
  const dispatch = useAppDispatch();
  const {signOut} = useFireBaseAuthentication();

  const mainMenuData: CardProfileMainMenuProps[] = [
    {
      title: t('Helpdesk'),
      icon: 'help',
      onPress: () => {
        appNavigation.navigate(Routes.PROFILE_HELPDESK);
      },
    },
    {
      title: t('Achievment'),
      icon: 'achievment',
      onPress: () => {
        appNavigation.navigate(Routes.PROFILE_ACHIEVEMENT, {
          state: {canGoBack: true},
        });
      },
    },
    {
      title: t('Notification'),
      icon: 'notification',
      count: 0,
      onPress: () => {
        appNavigation.navigate(Routes.PROFILE_NOTIFICATION, {
          state: {canGoBack: true},
        });
      },
    },
  ];

  const secondaryMenu: ProfileMenuProps[] = [
    /* {
          title: t('Personality quizz'),
          icon: 'personnality',
          onPress: () => navigation.navigate('ProfileQuizz'),
        }, */
    {
      title: t('My profile'),
      icon: 'profil',
      hide: isVodacomUser,
      onPress: () => {
        appNavigation.navigate(Routes.PROFILE_MY_PROFILE);
      },
    },
    {
      title: t('Subscription'),
      icon: 'abonnement',
      onPress: () => {
        appNavigation.navigate(Routes.PROFILE_SUBSCRIPTION);
      },
    },
    {
      title: t('Settings and preferences'),
      icon: 'parametre',
      onPress: () => {
        appNavigation.navigate(Routes.PROFILE_SETTINGS);
      },
    },
    {
      title: t('Bookmarks'),
      icon: 'favoris',
      onPress: () => {
        appNavigation.navigate(Routes.BOOKMARKS, {
          state: {canGoBack: true},
        });
      },
    },
    {
      title: t('Downloads'),
      icon: 'telechargement',
      disabled: true,
      onPress: () => {},
    },
    {
      title: t('Opportunity'),
      icon: 'opportunity',
      onPress: () => {
        appNavigation.navigate(Routes.PROFILE_OPPORTUNITIES);
      },
    },
    // {
    //   title: t('Application tutorial'),
    //   icon: 'help',
    //   onPress: () => {
    //     appNavigation.navigate(Routes.TUTORIAL);
    //   },
    // },
  ];

  const handleSignOut = async () => {
    try {
      setIsLoading(true);
      await session.handleExpiredSession();
      await signOut();
      trackTealiumSignout();
      dispatch(memberSlice.actions.setFirebaseUser(undefined));
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <View
      style={{
        height: '100%',
        backgroundColor: '#111216',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}>
      <View>
        {!isVodacomUser && <Typography variant="h2">{userName}</Typography>}
        <View
          style={{
            flexDirection: 'row',
            // justifyContent: 'space-between',
            // @ts-ignore
            gap: gap,
            marginTop: profile.verticalgap,
          }}>
          {mainMenuData.map((m, index) => (
            <View
              style={{minWidth: Metrics.horizontalScale(109), flex: 1}}
              key={`hm-${index}`}>
              <CardProfileMainMenu {...m} />
            </View>
          ))}
        </View>
        <View style={{marginTop: (isMobile ? 41 : 39) - 12}}>
          {secondaryMenu.map((s, index) =>
            s.hide ? null : (
              <View key={`sm-${index}`}>
                <ProfileMenu {...s} customStyle={[s.customStyle]} />
              </View>
            ),
          )}
        </View>
      </View>
      <View
        // @ts-ignore
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          paddingBottom: Metrics.verticalScale(isMobile ? 22 : 62),
          paddingTop: Metrics.verticalScale(12),
          position: 'sticky',
          bottom: -12,
          background: defaultTheme.page.background,
          alignItems: 'flex-end',
        }}>
        <View
          style={{
            // @ts-ignore
            gap: Metrics.horizontalScale(12),
          }}>
          {termCondition && (
            <Pressable
              style={{
                justifyContent: 'center',
                maxWidth: Metrics.horizontalScale(200),
              }}
              onPress={() => {
                appNavigation.navigate(Routes.TERMS_AND_CONDITION, {
                  state: {canGoBack: true, params: {type: 'termsConditions'}},
                });
              }}>
              <Typography variant="body3" color="gray">
                {termCondition.title}
              </Typography>
            </Pressable>
          )}
          {privacyPolicy && (
            <Pressable
              style={{
                justifyContent: 'center',
                maxWidth: Metrics.horizontalScale(200),
              }}
              onPress={() => {
                appNavigation.navigate(Routes.TERMS_AND_CONDITION, {
                  state: {
                    canGoBack: true,
                    params: {
                      type: 'privacyPolicy',
                    },
                  },
                });
              }}>
              <Typography variant="body3" color="gray">
                {privacyPolicy.title}
              </Typography>
            </Pressable>
          )}
        </View>
        <Pressable
          disabled={isLoading}
          style={{
            justifyContent: 'center',
          }}
          onPress={handleSignOut}>
          <Typography variant="body3">{t('Sign out')}</Typography>
        </Pressable>
      </View>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 3,
          }}>
          <CustomActivityIndicator />
        </div>
      )}
    </View>
  );
};

export default ProfileHome;
